const BAGGAGE_TYPES = new Set(["TROLLEY", "LUGGAGE", "BAGGAGE", "CHECKED", "CARRYON"]);

function normalizeList(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

function parseMoney(value) {
  if (value == null) return null;
  const n = Number.parseFloat(String(value).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

function parseWeightKg(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value > 0 ? value : null;
  if (typeof value === "string") {
    const m = value.match(/(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const n = Number.parseFloat(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (typeof value === "object") {
    const keys = [
      "kg",
      "weight",
      "maxWeight",
      "allowance",
      "value",
      "amount",
      "max",
      "checked",
      "carryOn",
      "hand",
    ];
    for (const key of keys) {
      const parsed = parseWeightKg(value?.[key]);
      if (parsed != null) return parsed;
    }
  }
  return null;
}

function isTruthyFlag(value) {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return false;
  const t = value.trim().toLowerCase();
  return t === "true" || t === "1" || t === "yes" || t === "included";
}

function classifyBaggageType(item) {
  const typeRaw = String(item?.type ?? item?.Type ?? "").trim().toUpperCase();
  const text = String(
    item?.description ?? item?.desc ?? item?.title ?? item?.name ?? ""
  )
    .trim()
    .toUpperCase();
  const merged = `${typeRaw} ${text}`;
  if (merged.includes("TROLLEY")) return "trolley";
  if (
    merged.includes("CARRY") ||
    merged.includes("HAND BAG") ||
    merged.includes("CABIN")
  ) {
    return "carryOn";
  }
  if (
    merged.includes("LUGGAGE") ||
    merged.includes("BAGGAGE") ||
    merged.includes("CHECKED")
  ) {
    return "checkedBag";
  }
  return null;
}

function typeLabelHe(type) {
  if (type === "trolley") return "טרולי";
  if (type === "carryOn") return "כבודת יד";
  return "מזוודה";
}

function includedSuffixHe(type) {
  return type === "trolley" ? "כלול" : "כלולה";
}

function normalizeSupplementCandidates(rawDeal) {
  const candidates = [
    rawDeal?.supplement,
    rawDeal?.supplements,
    rawDeal?.ancillaries?.supplement,
    rawDeal?.ancillaries?.supplements,
    rawDeal?.baggage?.supplement,
    rawDeal?.baggage?.supplements,
    rawDeal?.luggage?.supplement,
    rawDeal?.luggage?.supplements,
  ];
  return candidates.flatMap((c) => normalizeList(c)).filter(Boolean);
}

function normalizeSupplementWeightText(item) {
  const paramsRaw = item?.params;
  const params = Array.isArray(paramsRaw)
    ? paramsRaw
    : paramsRaw && typeof paramsRaw === "object"
      ? [paramsRaw]
      : [];
  const fromWeightText = String(item?.weightText || "").trim();
  if (fromWeightText) return fromWeightText.replace(/\s+/g, " ");
  const weight = params.find((p) => String(p?.name || "").toLowerCase() === "weight")?.value;
  const unit =
    params.find((p) => String(p?.name || "").toLowerCase() === "unit")?.value ?? "KG";
  if (weight != null && String(weight).trim() !== "") {
    return `${String(weight).trim()} ${String(unit).trim()}`.trim();
  }
  return "";
}

function parseSupplementFare(item) {
  const candidate = [
    item?.fare,
    item?.ourFare,
    item?.price,
    item?.amount,
    item?.ourPrice,
  ];
  for (const value of candidate) {
    if (value == null || String(value).trim() === "") continue;
    const parsed = parseMoney(value);
    if (parsed == null) return { kind: "unknown" };
    if (parsed === 0) return { kind: "zero" };
    return { kind: "positive", amount: parsed };
  }
  return { kind: "unknown" };
}

function collectBaggageSupplements(rawDeal) {
  const supplements = normalizeSupplementCandidates(rawDeal);
  return supplements
    .map((item) => {
      const baggageType = classifyBaggageType(item);
      if (!baggageType) return null;
      const typeToken = String(item?.type ?? item?.Type ?? "").trim().toUpperCase();
      if (typeToken && !BAGGAGE_TYPES.has(typeToken) && !typeToken.includes("BAG")) {
        return null;
      }
      return {
        item,
        baggageType,
        fare: parseSupplementFare(item),
      };
    })
    .filter(Boolean);
}

function includedLineFromSupplement(entry) {
  const { item, baggageType } = entry;
  const label = typeLabelHe(baggageType);
  const suffix = includedSuffixHe(baggageType);
  const weight = normalizeSupplementWeightText(item);
  const perPaxDirection =
    String(item?.category || "").trim().toUpperCase() === "PERFLIGHTANDPAX";
  return `${label}${weight ? ` ${weight}` : ""} ${suffix}${
    perPaxDirection ? " לנוסע לכיוון" : ""
  }`;
}

function normalizeFlightLegs(rawDeal) {
  const linkedFlights = rawDeal?.linkedFlights ?? rawDeal?.linkedflights ?? null;
  const flights = linkedFlights?.flight ?? rawDeal?.flight ?? rawDeal?.flightSegments;
  return normalizeList(flights)
    .flatMap((flight) => normalizeList(flight?.leg ?? flight))
    .filter((leg) => leg && typeof leg === "object");
}

function collectIncludedFromFlightSegments(rawDeal) {
  const legs = normalizeFlightLegs(rawDeal);
  if (legs.length === 0) return [];

  let maxHandKg = 0;
  let maxCheckedKg = 0;
  let hasHandIncludedFlag = false;
  let hasCheckedIncludedFlag = false;

  for (const leg of legs) {
    const handKg = parseWeightKg(
      leg?.maxHandLuggageWeight ??
        leg?.handLuggageWeight ??
        leg?.carryOnWeight ??
        leg?.carryOnKg ??
        leg?.carryOn?.weight ??
        leg?.baggageInfo?.carryOn ??
        leg?.includedBaggage?.carryOn ??
        leg?.luggage?.carryOn
    );
    const checkedKg = parseWeightKg(
      leg?.maxCheckedLuggageWeight ??
        leg?.checkedLuggageWeight ??
        leg?.checkedBaggage ??
        leg?.checkedBaggageWeight ??
        leg?.checkedBaggageKg ??
        leg?.baggageAllowance ??
        leg?.baggageInfo?.checked ??
        leg?.includedBaggage?.checked ??
        leg?.luggage?.checked
    );
    if (handKg != null && handKg > maxHandKg) maxHandKg = handKg;
    if (checkedKg != null && checkedKg > maxCheckedKg) maxCheckedKg = checkedKg;

    if (
      isTruthyFlag(leg?.handLuggageIncluded) ||
      isTruthyFlag(leg?.carryOnIncluded) ||
      isTruthyFlag(leg?.includedBaggage?.carryOnIncluded)
    ) {
      hasHandIncludedFlag = true;
    }
    if (
      isTruthyFlag(leg?.checkedLuggageIncluded) ||
      isTruthyFlag(leg?.checkedBaggageIncluded) ||
      isTruthyFlag(leg?.includedBaggage?.checkedIncluded)
    ) {
      hasCheckedIncludedFlag = true;
    }
  }

  const lines = [];
  if (maxHandKg > 0 || hasHandIncludedFlag) {
    lines.push(maxHandKg > 0 ? `טרולי ${maxHandKg} ק"ג כלול` : "טרולי כלול");
  }
  if (maxCheckedKg > 0 || hasCheckedIncludedFlag) {
    lines.push(
      maxCheckedKg > 0 ? `מזוודה ${maxCheckedKg} ק"ג כלולה` : "מזוודה כלולה"
    );
  }
  return lines;
}

function dedupeLines(lines) {
  const seen = new Set();
  const out = [];
  for (const line of lines) {
    const normalized = String(line || "").trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

/**
 * Helper אחיד ל־UI: 3 מצבים בלבד.
 * @param {object | null | undefined} rawDeal
 * @returns {{
 *   displayState: 'included' | 'paid_upgrade' | 'no_baggage',
 *   includedLines: string[],
 *   paidUpgradeOptionsCount: number,
 *   hasTrolley: boolean,
 *   hasCheckedBag: boolean,
 *   hasCarryOn: boolean,
 *   source: { fromSupplements: boolean, fromFlightSegments: boolean },
 * }}
 */
export function resolveFlightBaggageDisplayState(rawDeal) {
  const supplementRows = collectBaggageSupplements(rawDeal);
  const includedFromSupplements = supplementRows.filter(
    ({ item, fare }) =>
      isTruthyFlag(item?.include) ||
      isTruthyFlag(item?.included) ||
      fare.kind === "zero"
  );
  const paidUpgradeRows = supplementRows.filter(({ fare }) => fare.kind === "positive");
  const includedFromFlightSegments = collectIncludedFromFlightSegments(rawDeal);
  const includedLines = dedupeLines([
    ...includedFromSupplements.map(includedLineFromSupplement),
    ...includedFromFlightSegments,
  ]);

  const hasTrolley =
    includedLines.some((line) => line.includes("טרולי")) ||
    supplementRows.some((row) => row.baggageType === "trolley");
  const hasCheckedBag =
    includedLines.some((line) => line.includes("מזוודה")) ||
    supplementRows.some((row) => row.baggageType === "checkedBag");
  const hasCarryOn =
    includedLines.some((line) => line.includes("כבודת יד")) ||
    supplementRows.some((row) => row.baggageType === "carryOn");

  if (includedLines.length > 0) {
    return {
      displayState: "included",
      includedLines,
      paidUpgradeOptionsCount: paidUpgradeRows.length,
      hasTrolley,
      hasCheckedBag,
      hasCarryOn,
      source: {
        fromSupplements: includedFromSupplements.length > 0,
        fromFlightSegments: includedFromFlightSegments.length > 0,
      },
    };
  }
  if (paidUpgradeRows.length > 0) {
    return {
      displayState: "paid_upgrade",
      includedLines: [],
      paidUpgradeOptionsCount: paidUpgradeRows.length,
      hasTrolley,
      hasCheckedBag,
      hasCarryOn,
      source: {
        fromSupplements: true,
        fromFlightSegments: false,
      },
    };
  }
  return {
    displayState: "no_baggage",
    includedLines: [],
    paidUpgradeOptionsCount: 0,
    hasTrolley: false,
    hasCheckedBag: false,
    hasCarryOn: false,
    source: {
      fromSupplements: false,
      fromFlightSegments: false,
    },
  };
}

/**
 * @typedef {'unknown' | 'included' | 'optional'} FlightBaggageFlowStatus
 */

/**
 * ניתוח לתאימות לאחור במסכים קיימים.
 * @param {object | null | undefined} rawDeal
 * @returns {{
 *   status: FlightBaggageFlowStatus,
 *   badgeLabelHe: string | null,
 *   hasTrolley: boolean,
 *   hasCheckedBag: boolean,
 * }}
 */
export function analyzeFlightBaggageSupplementsForFlow(rawDeal) {
  const resolved = resolveFlightBaggageDisplayState(rawDeal);
  if (resolved.displayState === "included") {
    return {
      status: "included",
      badgeLabelHe: resolved.includedLines.join(" · "),
      hasTrolley: resolved.hasTrolley,
      hasCheckedBag: resolved.hasCheckedBag,
    };
  }
  if (resolved.displayState === "paid_upgrade") {
    return {
      status: "optional",
      badgeLabelHe: null,
      hasTrolley: resolved.hasTrolley,
      hasCheckedBag: resolved.hasCheckedBag,
    };
  }
  return {
    status: "unknown",
    badgeLabelHe: null,
    hasTrolley: false,
    hasCheckedBag: false,
  };
}

/** @param {{ selectedFlightOffer?: { rawDeal?: object } | null } | null | undefined} bookingState */
export function shouldSkipBaggageStep(bookingState) {
  const raw = bookingState?.selectedFlightOffer?.rawDeal ?? null;
  return resolveFlightBaggageDisplayState(raw).displayState !== "paid_upgrade";
}

export function hasSelectableBaggageExtras(rawDeal) {
  return resolveFlightBaggageDisplayState(rawDeal).displayState === "paid_upgrade";
}

export function isBaggageIncludedForFlight(rawDeal) {
  return resolveFlightBaggageDisplayState(rawDeal).displayState === "included";
}

/**
 * לממשק כרטיס תוצאות — רק כשכבודה כלולה.
 */
export function getFlightBaggageIncludedBadge(rawDeal) {
  const resolved = resolveFlightBaggageDisplayState(rawDeal);
  if (resolved.displayState !== "included" || resolved.includedLines.length === 0) {
    return null;
  }
  return { labelHe: resolved.includedLines.join(" · ") };
}
